import { useState, useEffect } from "react";

import capitalize from "lodash/capitalize";
import { useIntl } from "react-intl";
import sanitizeHtml from "sanitize-html";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { AnimatedJiraTourProps } from "@/components/product-demos/animated-jira-tour/src/animated-jira-tour";
import type { Edition } from "@/components/signup/utils/products";
import { BannerProps } from "@/components/wpl/banner-alert/src";
import { IndividualTabPropsCustom } from "@/components/wpl/tab-group/src";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import ImageChecklistSide from "@/patterns/image-checklist-side";
import { PatternThreeAlternatingImageTextProps } from "@/patterns/three-alternating-image-text/src";
import { SideBySideProps } from "@/patterns/wpl/simple-image-text-side/src";
import {
    PaidLandingTemplate,
    PaidLandingProps
} from "@/templates/paid-landing/src/paid-landing.template";
import type {
    HeroProps,
    PaidLandingTypes
} from "@/templates/paid-landing/src/paid-landing.template";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import { Cookie } from "@/utils/cookie";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
import {
    JourneyLocalStoragePersona,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";

import { AuxiaResponse } from "../auxia/get-auxia-jsm-paid-lp-treatment";
import {
    isAuxiaResponseMalformed,
    isAuxiaServedPage
} from "../auxia/is-auxia-served-page";
import {
    convertDateTimeToInteractionTimeMicros,
    sendAnalyticsToAuxia
} from "../auxia/send-analytics-to-auxia";

const LANDING_TYPE: PaidLandingTypes = "jira-service-management-anchor";

interface HeroConfiguration extends Partial<HeroProps> {
    useNewHero: boolean;
    showAnimatedHero: boolean;
}

export type PaidLandingPageSporkVariants =
    | "asset"
    | "automation"
    | "competitors"
    | "csm"
    | "devops"
    | "esm"
    | "features"
    | "features-cerebro"
    | "incidentchangemgmt"
    | "itsm"
    | "service-desk"
    | "ticketing"
    | "marketing"
    | "engineering"
    | "hr"
    | "legal"
    | "operations"
    | "design"
    | "customerservice";

export type AnchorPageProps = {
    persona?: PaidLandingPersonaTypes;
    teamType?: JourneyLocalStoragePersona["team"];
    pageId: string;
    anchorContents?: AnchorContentsResponse | null;
    pageVariant: PaidLandingPageSporkVariants;
    animatedJiraTourProps?: AnimatedJiraTourProps;
    bannerAlertProps?: BannerProps;
    bannerProps?: SideBySideProps;
    signupEdition?: Edition;
    auxiaResponse?: AuxiaResponse | null;
};

export const JiraSoftwarePagePaidLpJsmAnchor = ({
    anchorContents,
    persona,
    teamType,
    pageId,
    pageVariant,
    animatedJiraTourProps,
    bannerAlertProps,
    signupEdition,
    auxiaResponse
}: AnchorPageProps) => {
    const intl = useIntl();
    const enableCustomerServiceManagementLogo =
        checkFeatureFlagGate(featureFlags.enableCsmAjLogo) &&
        pageId === "anchor-lp-jsm-csm";
    const productHref = useLocalizedHref(
        enableCustomerServiceManagementLogo
            ? "/software/customer-service-management"
            : "/software/jira/service-management"
    );
    const [content, setContent] = useState<AnchorContentsResponse | undefined>(
        undefined
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const { setAnchorLocalStorage } = useAnchorLocalStorage();

    const customHeadline: string = intl.formatMessage({
        id: "jira.servicemanagement.anchor.paidlpjsmitsm.variantb",
        defaultMessage: "End bad service management"
    });

    const anonymousId = decodeURIComponent(
        Cookie.readCookie("ajs_anonymous_id") || ""
    ).replace(/['"]+/g, "");

    if (isAuxiaServedPage(auxiaResponse)) {
        createAnalyticsEvent({
            name: "getAuxiaJsmPaidLpTreatmentSuccess",
            action: "getAuxiaJsmPaidLpTreatmentSuccess",
            actionSubject: "auxiaPage",
            eventType: "operational",
            source: window.location.pathname,
            attributes: {
                pageUrl: window.location.href,
                anonymousId: anonymousId,
                auxiaResponse: auxiaResponse
            }
        }).fire();
    } else if (isAuxiaResponseMalformed(auxiaResponse)) {
        createAnalyticsEvent({
            name: "getAuxiaJsmPaidLpTreatmentFailure",
            action: "getAuxiaJsmPaidLpTreatmentFailure",
            actionSubject: "auxiaPage",
            eventType: "operational",
            source: window.location.pathname,
            attributes: {
                pageUrl: window.location.href,
                anonymousId: anonymousId,
                auxiaResponse: auxiaResponse
            }
        }).fire();
    }

    useEffect(() => {
        setAnchorLocalStorage({
            persona,
            teamTypeInferredMl: teamType,
            team: ""
        });

        if (isAuxiaServedPage(auxiaResponse)) {
            setContent(auxiaResponse.auxiaTreatment);

            sendAnalyticsToAuxia({
                anonymousId: anonymousId,
                treatmentTrackingId: auxiaResponse.treatmentTrackingId,
                treatmentId: auxiaResponse.treatmentId,
                interactionType: "VIEWED",
                interactionTimeMicros: convertDateTimeToInteractionTimeMicros(
                    Date.now()
                )
            });
        } else {
            setContent(anchorContents);
        }
        // We only want to run this useEffect once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!content) {
        // TODO: loading UI for static renders
        return <div />;
    }

    const tabsProps = getTabProps(content);
    const tabsHasTestimonials =
        tabsProps && tabsProps.tabs && tabsProps.tabs[0]
            ? tabsProps.tabs[0].isIncludeTestimony
            : null;

    const heroProps = getHeroProps({
        resp: content,
        pageVariant,
        customHeadline,
        isAuxiaServedPage: !!isAuxiaServedPage(auxiaResponse)
    });

    const heroImageSubheader = heroProps.tertiaryText;

    const servCoBanner: BannerProps = {
        identifier: "jira-service-management.banner-alert.service-collection",
        text: intl.formatMessage({
            id: "jira-service-management.banner-alert.service-collection.text",
            defaultMessage:
                "Jira Service Management is now more powerful than ever as part of Service Collection. Unlock exceptional service experiences with Rovo, Assets, and our new Customer Service Management app."
        }),
        linkText: intl.formatMessage({
            id: "jira-service-management.banner-alert.service-collection.cta",
            defaultMessage: "Try it now"
        }),
        linkUrl: "/try/cloud/signup?bundle=service-collection&edition=premium",
        hasProductNavBar: true
    };

    const paidJSMBannerAlertProps = checkFeatureFlagGate(
        featureFlags.gwp8577ServiceCollectionBanners
    )
        ? servCoBanner
        : bannerAlertProps;

    const logoProductName = enableCustomerServiceManagementLogo
        ? "customerservicemanagement"
        : "jiraservicemanagement";

    const logoAriaLabel = enableCustomerServiceManagementLogo
        ? intl.formatMessage({
              id: "wacCustomerServiceManagementNavigation.logo-label",
              defaultMessage: "Customer Service Management logo"
          })
        : intl.formatMessage({
              id: "wacJiraServiceManagementNavigation.logo-label",
              defaultMessage: "Jira Service Management logo"
          });

    const props: PaidLandingProps = {
        landingType: LANDING_TYPE,
        pageId,
        animatedJiraTourProps,
        showHorizontalRule: !tabsHasTestimonials,
        productNavProps: {
            productHref: productHref,
            productName: logoProductName,
            ariaLabel: logoAriaLabel,
            dataProductKey: "jiraServiceManagement",
            showFreeHrefButton: false,
            logoLinkWrapperWidth: "279px"
        },
        heroProps: {
            ...heroProps,
            signup: {
                ...heroProps.signup,
                edition: signupEdition
            }
        },
        tabsProps: tabsProps,
        bannerAlertProps: paidJSMBannerAlertProps,
        bannerProps: getBannerProps(content),
        ctaProps: getCtaProps(content),
        testimonialProps: getTestimonialProps(content),
        alternatingTextAndImageProps: getAlternatingTextAndImageProps(content),
        heroImageSubheader,
        heroBgColor: "bgBlackBlue",
        hasRebrandedSignupFormStyles: true,
        auxiaResponse: auxiaResponse
    };

    return <PaidLandingTemplate {...props} />;
};

const heroFeatureMatrix: Record<string, HeroConfiguration> = {
    "asset": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "automation": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "competitors": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "csm": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "devops": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },

    "esm": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "features": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "features-cerebro": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "incidentchangemgmt": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "incident-changemgmt": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "itsm": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "service-desk": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "ticketing": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "marketing": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "engineering": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "hr": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "legal": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "operations": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "design": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    },
    "customerservice": {
        useNewHero: true,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: true,
        showStaticImage: false
    }
};

const getHeroFeatures = (pageVariant: string): HeroConfiguration => {
    const defaultFeatures: HeroConfiguration = {
        useNewHero: false,
        showAnimatedHero: false,
        animatedSocialBanner: false,
        darkSolidTheme: false,
        showStaticImage: false
    };

    return heroFeatureMatrix[pageVariant] || defaultFeatures;
};

const getHeroProps = ({
    resp,
    pageVariant,
    customHeadline
}: {
    resp: AnchorContentsResponse;
    pageVariant: string;
    customHeadline?: string;
    isUserLoggedIn?: boolean;
    isAuxiaServedPage?: boolean;
    // eslint-disable-next-line complexity
}): PaidLandingProps["heroProps"] => {
    const defaultProps: PaidLandingProps["heroProps"] = {
        theme: "light-on-dark",
        headerText: "",
        secondaryText: "",
        signup: {
            bundle: "jira-service-management",
            edition: "free",
            featureFlags: {
                enableDefaultExpandedEmail: false,
                enableDeferredFullName: true
            }
        }
    };
    if (!resp || !resp[0]) {
        return defaultProps;
    }
    const { headline } = resp[0].node;
    const nodeName = resp[0].node.name;
    const data = headline && headline[0] ? headline[0] : null;

    const planBenefits =
        data?.planBenefits && data?.planBenefits[0]
            ? data?.planBenefits[0]
            : null;
    const supportingAsset = planBenefits?.supportingAsset;
    const features = getHeroFeatures(pageVariant);

    const getImage = (url?: string | null, description?: string | null) =>
        url && description
            ? {
                  src: url ? url : "",
                  alt: description || ""
              }
            : undefined;

    const getFeaturesRawHtml = () =>
        planBenefits &&
        planBenefits.planFeatures &&
        planBenefits.planFeatures[0]
            ? planBenefits.planFeatures[0].description || ""
            : "";

    return {
        ...defaultProps,
        ...features,
        headerText:
            customHeadline &&
            customHeadline.length > 0 &&
            nodeName == "paid-lp-jsm-itsm" &&
            !isAuxiaServedPage
                ? customHeadline
                : data?.name || "",
        secondaryText: data?.subheading || "",
        image: getImage(supportingAsset?.url, supportingAsset?.description),
        featuresRawHtml: getFeaturesRawHtml(),
        tertiaryText: planBenefits?.description || ""
    };
};

const getTabProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["tabsProps"] => {
    const defaultProps: PaidLandingProps["tabsProps"] = {
        dataProductKey: "jiraSoftware-PaidLpJiraPm",
        headerText: "",
        tabStyles: {
            defaultBG: wacToken("color.neutral.light200"),
            defaultText: wacToken("color.extended.blue900"),
            hoverBG: wacToken("color.neutral.light200"),
            hoverText: wacToken("color.extended.blue900"),
            activeBG: wacToken("color.extended.blue100"),
            activeText: wacToken("color.extended.blue900")
        },
        styles: {
            "--lp-tabs-hover": wacToken("color.extended.blue700"),
            "--lp-tabs-unselected": wacToken("color.extended.blue900")
        },
        tabs: undefined
    };

    if (!resp || !resp[0]) {
        return defaultProps;
    }

    const tabs = getTabs(resp);
    const primaryMessage = getPrimaryMessage(resp);

    return {
        ...defaultProps,
        landingType: LANDING_TYPE,
        headerText: primaryMessage?.name || "",
        tabs
    };
};

const getBannerProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["bannerProps"] => {
    const banner = getBanner(resp);
    if (!banner) return undefined;
    return {
        headerText: (
            <div
                dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(banner?.text || "")
                }}
            />
        ),
        isBannerRichText: true,
        imageLink: banner?.bannerImage?.url || "",
        imageAltText: banner?.bannerImage?.description || "imageAltText",
        imagePosition: "left",
        backgroundColor: wacToken("color.extended.blue200"),
        ctaProps: {
            buttonProps: {
                text: banner?.urlText || "",
                url: banner?.url || "",
                color: "darkblue",
                type: "secondary"
            }
        },
        margin: "60px 0"
    };
};

const getCtaProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["ctaProps"] => {
    const cta = getCta(resp);
    return {
        headerText: cta?.valueProposition || "",
        ctaProps: {
            buttonProps: {
                color: "yellow",
                url: cta?.url || "",
                text: cta?.text || "",
                dataEventProps: {
                    "data-event": "clicked",
                    "data-event-container": "ctaSection",
                    "data-event-component": "linkButton",
                    "data-product-key": "jiraSoftware"
                }
            }
        },
        backgroundProperties: {
            textColor: wacToken("color.neutral.light0")
        }
    };
};

export const getTestimonialProps = (
    resp: AnchorContentsResponse
): PaidLandingProps["testimonialProps"] | undefined => {
    if (!resp || !resp[0]) {
        return undefined;
    }
    const { socialProof } = resp[0].node;
    const data = socialProof && socialProof[0] ? socialProof[0] : null;

    if (!data) {
        return undefined;
    }

    return {
        headerText: data?.name,
        logos:
            data?.customers?.map((customer) => {
                return {
                    src:
                        customer &&
                        customer.logo &&
                        customer?.logo[0] &&
                        customer?.logo[0].url
                            ? customer?.logo[0].url
                            : "",
                    alt:
                        customer &&
                        customer.logo &&
                        customer?.logo[0] &&
                        customer?.logo[0].description
                            ? customer?.logo[0].description
                            : "",
                    lazy: true
                };
            }) || []
    };
};

const getAlternatingTextAndImageProps = (
    resp: AnchorContentsResponse
): PatternThreeAlternatingImageTextProps | undefined => {
    const results = getResults(resp);
    function generateProps(
        result: {
            asset: {
                contentType: string | null;
                fileName: string | null;
                title: string | null;
                details: string | null;
                description: string | null;
                url: string | null;
            } | null;
            subheading: string | null;
            name: string | null;
            id: string;
        } | null,
        index: number
    ) {
        // this shouldn't go past
        const countMap = ["one", "two", "three", "four"];
        const count = capitalize(`${countMap[index]}`);
        const position = index % 2 === 0 ? "left" : "right";

        return {
            [`row${count}Header`]: result?.name || "",
            [`row${count}Body`]: result?.subheading || "",
            [`row${count}ImageLink`]: result?.asset?.url, //getLocalizedImage("row one", intl.locale),
            [`row${count}ImageAltText`]: result?.asset?.description || "",
            [`row${count}ImagePosition`]: position
        };
    }

    const contentBlocks = results
        ?.map((result, index) => generateProps(result, index))
        .slice(0, 4) // ensure max of 4 results so we don't break the pattern
        .filter((result) => !!result)
        .reduce((acc, curr) => {
            return {
                ...acc,
                ...curr
            };
        });

    return {
        imageWidth: "100%",
        imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
        imageTextSideRowTwoPadding: "16px 0",
        imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
        ...contentBlocks
    };
};

const getPrimaryMessage = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { primaryMessage } = resp[0].node;
    const data = primaryMessage && primaryMessage[0] ? primaryMessage[0] : null;

    return data;
};

const getTabs = (
    resp: AnchorContentsResponse
): IndividualTabPropsCustom[] | undefined => {
    const primaryMessage = getPrimaryMessage(resp);

    if (!primaryMessage || !primaryMessage?.supportingExample) {
        return undefined;
    }

    return (
        primaryMessage.supportingExample
            .filter((tab) => tab !== null)
            // eslint-disable-next-line complexity
            .map((tab) => {
                const supportingAsset = tab?.supportingAsset;
                const advocateQuote = tab?.advocateQuote;
                const isIncludeTestimony = !!tab?.advocateQuote;

                return {
                    textLabel: tab?.name || "",
                    id: tab?.name || "",
                    isIncludeTestimony: isIncludeTestimony,
                    component: (
                        <ImageChecklistSide
                            isShowTestimonyBorder={isIncludeTestimony}
                            isIncludeTestimony={isIncludeTestimony}
                            testimonyIsUsingTextQuotationMarks={false}
                            key={tab?.name}
                            title={tab?.heading || ""}
                            rawHtmlContent={tab?.proofpoint || ""}
                            imageSrc={supportingAsset?.url || ""}
                            imageAlt={supportingAsset?.description || ""}
                            quoteBlurb={advocateQuote?.advocateQuote || ""}
                            quoteName={advocateQuote?.advocateName || ""}
                            quoteCompany={advocateQuote?.advocateJobTitle || ""}
                            testimonyImageSrc={
                                advocateQuote?.advocateHeadshot?.url || ""
                            }
                            testimonyImageAlt={
                                advocateQuote?.advocateHeadshot?.description ||
                                ""
                            }
                            isSmallQuote={true}
                            isIncludeHorizontalMobileRule={false}
                        />
                    )
                };
            })
    );
};

const getResults = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { results } = resp[0].node;
    const data = results && results.length ? results : null;

    return data;
};

const getCta = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { callToAction } = resp[0].node;
    const data = callToAction && callToAction[0] ? callToAction[0] : null;

    return data;
};

const getBanner = (resp: AnchorContentsResponse) => {
    if (!resp || !resp[0]) {
        return null;
    }
    const { banner } = resp[0].node;
    const data = banner && banner[0] ? banner[0] : null;

    return data;
};
